<template>
  <VDialogV3
    id="product-purchase-analytics-dialog"
    position="center"
    width="1400px"
    :title="'Аналитика закупок: ' + product.name"
    :loading="isLoading"
    :class="{ dark: isDark }"
    hide-footer
    hide-feed
  >
    <VChart
      :from="period.from"
      :to="period.to"
      :height="480"
      :loading="chart.ratesLoadingCount > 0"
      :value-unit="`${$core.utils.money.displayCurrency?.code ?? '--'}/${product.unit?.name ?? '--'}`"
      :volume-unit="product.unit?.name ?? ''"
      :selected-period="selectedMonth === null ? null : { from: selectedMonth, to: selectedMonth.addMonths(1) }"
      @update:dark="isDark = $event"
    >
      <template #toolbar>
        <span class="description">
          {{ period.from.toString() }} — {{ period.to.toString() }}
        </span>

        <VButtonGroup>
          <VButton
            v-for="preset in PERIOD_PRESETS"
            :key="preset.id"
            :theme="preset.id === periodPresetId ? 'primary' : 'default'"
            @click="periodPresetId = preset.id"
          >
            {{ preset.name }}
          </VButton>
        </VButtonGroup>
      </template>

      <VChartBars label="Объём заказов" color="green" :bars="chart.orderVolumes"/>
      <VChartStepLine label="Бюджетная цена" color="red" dashed fill :steps="chart.budgetSteps"/>
      <VChartRanges label="Тендеры" color="blue" :ranges="chart.tenders"/>
      <VChartPoints label="Заказы" color="yellow" :points="chart.orders"/>

      <template v-if="chart.ratesMissingCount > 0" #description>
        Записей без курса на дату: {{ chart.ratesMissingCount }} — на графике не показаны
      </template>
    </VChart>

    <footer class="chart-data">
      <header>
        <div class="toggle" @click="toggleData">
          <VIcon :material="isDataExpanded === true ? 'expand_less' : 'expand_more'"/>

          <span>Данные графика</span>

          <span class="description">
            заказов {{ tableOrders.length }}, тендеров {{ tableTenders.length }}
          </span>
        </div>

        <!-- Период таблиц справа; выбранный кликом месяц — фильтр, который можно сбросить -->
        <span :class="['period', { filtered: selectedMonth !== null }]">
          <VIcon v-if="selectedMonth !== null" material="filter_alt" theme="outlined"/>
          {{ tableRange.from.toString() }} — {{ tableRange.to.addDays(-1).toString() }}
        </span>

        <VLink v-if="selectedMonth !== null" @click="selectedMonth = null">Сбросить</VLink>
      </header>

      <template v-if="isDataExpanded === true">
        <h4>Заказы</h4>

        <VDataGridV2 :data="tableOrders">
          <!-- Номер позиции ссылкой и дата создания под ним — две строки, без номера заказа, чтобы не раздувать высоту строки -->
          <VDataGridColumnV2 id="orderId" width="100px">
            <template #label>
              №
              <div class="nowrap description">Создан</div>
            </template>

            <template #default="{ row }">
              <VLink @click="OrdersService.showOrderPositionCardById(row.order.id)">
                {{ $core.utils.string.formatWithDelimiter(row.order.id) }}
              </VLink>
              <div class="nowrap description">{{ row.order.createdAt.toString() }}</div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="seller" label="Поставщик" width="200px" content-class="ellipsis">
            <template #default="{ row }">{{ row.order.sellerName }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="price" :label="`Цена, ${$core.utils.money.displayCurrency?.code ?? '--'}`" width="140px" content-align="right">
            <template #default="{ row }">{{ row.price?.format($core.utils.money.displayCurrency, product.unit) ?? '✕' }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="qty" label="Количество" width="140px" content-align="right">
            <template #default="{ row }">{{ row.order.qty.formatSmart(product.unit) }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="vat" label="НДС" width="110px">
            <template #default="{ row }">{{ row.order.vatNote }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="originalPrice" label="В валюте заказа" width="140px" content-align="right">
            <template #default="{ row }">{{ row.order.price.format(row.order.currencyCode, product.unit) }}</template>
          </VDataGridColumnV2>
        </VDataGridV2>

        <h4>Тендеры</h4>

        <VDataGridV2 :data="tableTenders">
          <VDataGridColumnV2 id="number" label="Тендер" width="90px" content-align="center">
            <template #default="{ row }">
              <VLink @click="TendersService.showTenderCardById(row.tender.id)">{{ row.tender.id }}</VLink>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="period" label="Период" width="190px" content-align="center">
            <template #default="{ row }">{{ row.period }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="startPrice" label="Стартовая цена" width="140px" content-align="right">
            <template #default="{ row }">{{ row.startPrice?.format($core.utils.money.displayCurrency, product.unit) ?? '✕' }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="bestPrice" label="Лучшая ставка" width="140px" content-align="right">
            <template #default="{ row }">{{ row.bestPriceText }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="diff" label="Изменение" width="100px" content-align="right">
            <template #default="{ row }">{{ row.diffText }}</template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="sellersCount" label="Участников" width="100px" content-align="right">
            <template #default="{ row }">{{ row.tender.sellersCount }}</template>
          </VDataGridColumnV2>
        </VDataGridV2>
      </template>
    </footer>
  </VDialogV3>
</template>

<style lang="scss" src="./ProductPurchaseAnalyticsDialog.scss"/>

<script lang="ts">
import Core                from "@/core/Core";
import DateSimple          from "@/core/types/DateSimple";
import Decimal             from "decimal.js";
import Price               from "@/core/types/Price";
import Qty                 from "@/core/types/Qty";
import BudgetPricesService from "@/domains/BudgetPrices/BudgetPricesService";
import type BudgetPrice    from "@/domains/BudgetPrices/BudgetPrice";
import OrdersService       from "@/domains/Orders/OrdersService";
import type OrderPricePoint  from "@/domains/Orders/OrderPricePoint";
import TendersService      from "@/domains/Tenders/TendersService";
import type TenderPricePoint from "@/domains/Tenders/TenderPricePoint";
import type Product        from "@/domains/Products/Product";
import ChartPoint          from "@/lib/components/VChart/ChartPoint";
import ChartBar            from "@/lib/components/VChart/ChartBar";
import ChartStep           from "@/lib/components/VChart/ChartStep";
import ChartRange          from "@/lib/components/VChart/ChartRange";
import ChartAction         from "@/lib/components/VChart/ChartAction";

/** Пресеты периода: по умолчанию вся история; «3 года» — 3 года назад и год вперёд, бюджетные цены заводят на полгода-год */
const PERIOD_PRESETS = [
  { id: 'threeYears', name: '3 года' },
  { id: 'year',       name: 'Год' },
  { id: 'all',        name: 'Всё' },
];

/**
 * Аналитика закупок номенклатуры: заказы точками и объёмом по месяцам, тендеры отрезками «стартовая → лучшая ставка»,
 * бюджетная цена ступенями. Всё приведено к валюте отображения: заказы по курсу на дату заказа,
 * тендеры — на начало тендера, бюджетная цена — на сегодня. Курсы догружаются сами, график перерисовывается реактивно.
 * Цены как в документах: тендер — полная цена с отсрочкой, заказ — обычная цена, НДС подписан в тултипе.
 */
export default defineComponent({
  name: "ProductPurchaseAnalyticsDialog",

  props: {
    product: {
      type: Object as PropType<Product>,
      required: true,
    },
  },

  setup() {
    return {
      PERIOD_PRESETS,
      OrdersService,
      TendersService,
    }
  },

  data() {
    return {
      orders: [] as OrderPricePoint[],
      tenders: [] as TenderPricePoint[],
      budgetPrices: [] as BudgetPrice[],
      periodPresetId: 'all',

      /** Тёмная тема графика — карточка перекрашивается вместе с ним */
      isDark: false,

      /** Месяц, выбранный кликом по колонке объёма; null — данные за весь период графика */
      selectedMonth: null as DateSimple | null,

      /** Таблица данных под графиком раскрыта */
      isDataExpanded: false,
      isLoading: true,
    }
  },

  async mounted() {
    const [budgetPrices, orders, tenders] = await Promise.all([
      BudgetPricesService.getHistory(this.product.id),
      OrdersService.getPriceHistory(this.product.id),
      TendersService.getPriceHistory(this.product.id),
    ]);

    this.budgetPrices = budgetPrices;
    this.orders = orders;
    this.tenders = tenders;
    this.isLoading = false;
  },

  computed: {
    period(): { from: DateSimple; to: DateSimple } {
      const today = DateSimple.today();

      if (this.periodPresetId === 'year') {
        return { from: today.addYears(-1), to: today.addMonths(6) };
      } else if (this.periodPresetId === 'all' && (this.orders.length > 0 || this.tenders.length > 0 || this.budgetPrices.length > 0)) {
        // От первой записи до полугода после последней бюджетной цены
        const starts: Date[] = [
          ...this.orders.map(order => order.createdAt),
          ...this.tenders.map(tender => tender.endAt),
          ...this.budgetPrices.map(budgetPrice => budgetPrice.validityPeriod.start!),
        ];

        let from = starts[0];
        let to: Date = today;

        for (const start of starts) {
          if (start.getTime() < from.getTime()) {
            from = start;
          }

          if (start.getTime() > to.getTime()) {
            to = start;
          }
        }

        return { from: new DateSimple(from).startOfMonth(), to: new DateSimple(to).addMonths(6) };
      } else {
        return { from: today.addYears(-3), to: today.addYears(1) };
      }
    },

    /**
     * Ряды графика в валюте отображения.
     *
     * Курс берётся из реактивного кэша (как formatInDisplayCurrency в реестре тендеров):
     * getRateFallbackMark сам ставит недостающие курсы в очередь, пока они едут — график показывает загрузку.
     * Запись, курса на дату которой нет и не будет, на график не попадает — считается отдельно.
     */
    chart(): {
      orders: ChartPoint[];
      tenders: ChartRange[];
      orderVolumes: ChartBar[];
      budgetSteps: ChartStep[];
      orderRows: { order: OrderPricePoint; price: Price | null }[];
      tenderRows: { tender: TenderPricePoint; period: string; startPrice: Price | null; bestPriceText: string; diffText: string }[];
      ratesLoadingCount: number;
      ratesMissingCount: number;
    } {
      const money = Core.utils.money;
      const displayCurrency = money.displayCurrency;
      const unit = this.product.unit;

      const result = {
        orders: [] as ChartPoint[],
        tenders: [] as ChartRange[],
        orderVolumes: [] as ChartBar[],
        budgetSteps: [] as ChartStep[],
        orderRows: [] as { order: OrderPricePoint; price: Price | null }[],
        tenderRows: [] as { tender: TenderPricePoint; period: string; startPrice: Price | null; bestPriceText: string; diffText: string }[],
        ratesLoadingCount: 0,
        ratesMissingCount: 0,
      };

      if (displayCurrency === null) {
        return result;
      }

      /** Цена в валюте отображения; курса ещё нет или не будет — null, и запись учитывается в счётчике */
      const convert = (price: Price, currencyCode: string, date: Date): Price | null => {
        const mark = money.getRateFallbackMark(currencyCode, displayCurrency, date);

        if (mark === null) {
          return money.convertSync(price, currencyCode, displayCurrency, date);
        } else if (mark === money.RATE_MISSING_MARK) {
          result.ratesMissingCount++;
          return null;
        } else {
          result.ratesLoadingCount++;
          return null;
        }
      };

      // Помесячная сводка по заказам для панели объёма: количество сделок и объём — по всем заказам,
      // цены — только по тем, что удалось пересчитать в валюту отображения
      const months = new Map<number, {
        count: number;
        qty: Qty | null;
        pricedQty: Decimal;
        pricedAmount: Decimal;
        minPrice: Price | null;
        maxPrice: Price | null;
      }>();

      for (const order of this.orders) {
        const monthStart = order.createdAt.startOfMonth().getTime();

        let month = months.get(monthStart);

        if (month === undefined) {
          month = { count: 0, qty: null, pricedQty: new Decimal(0), pricedAmount: new Decimal(0), minPrice: null, maxPrice: null };
          months.set(monthStart, month);
        }

        month.count++;

        if (month.qty === null) {
          month.qty = order.qty;
        } else {
          month.qty = month.qty.plus(order.qty);
        }

        const price = convert(order.price, order.currencyCode, order.createdAt);

        result.orderRows.push({ order, price });

        if (price !== null) {
          const lines = [
            `${order.tenderId === null ? 'Заказ' : 'Заказ по тендеру'} № ${order.orderId} от ${order.createdAt.toString()}`,
            `Поставщик: ${order.sellerName}`,
            `Цена: ${price.format(displayCurrency, unit)}`,
            `Количество: ${order.qty.formatSmart(unit)}`,
            `НДС: ${order.vatNote}`,
          ];

          if (order.currencyCode !== displayCurrency.code) {
            lines.push(`В валюте заказа: ${order.price.format(order.currencyCode, unit)}`);
          }

          result.orders.push(new ChartPoint(
            order.createdAt,
            price.toNumber(),
            lines.join('\n'),
            new ChartAction(String(order.orderId), () => OrdersService.showOrderPositionCardById(order.id)),
          ));

          month.pricedQty = month.pricedQty.plus(order.qty.value);
          month.pricedAmount = month.pricedAmount.plus(price.value.times(order.qty.value));

          if (month.minPrice === null || price.lessThan(month.minPrice) === true) {
            month.minPrice = price;
          }

          if (month.maxPrice === null || price.greaterThan(month.maxPrice) === true) {
            month.maxPrice = price;
          }
        }
      }

      // Столбик на каждый месяц периода до текущего, включая пустые: их колонки тоже ловят наведение.
      // Будущие месяцы не заполняются — «заказов не было» про них неправда
      const lastMonth = DateSimple.today().startOfMonth();

      // Завершённые тендеры по месяцу закрытия — для сводки периода
      const monthlyTendersCount = new Map<number, number>();

      for (const tender of this.tenders) {
        const monthStart = new DateSimple(tender.endAt).startOfMonth().getTime();

        monthlyTendersCount.set(monthStart, (monthlyTendersCount.get(monthStart) ?? 0) + 1);
      }

      for (let start = this.period.from.startOfMonth(); start.getTime() <= lastMonth.getTime() && start.getTime() <= this.period.to.getTime(); start = start.addMonths(1)) {
        const end = start.addMonths(1);
        const month = months.get(start.getTime());
        const lines = [`Период: ${start.toString()} — ${end.addDays(-1).toString()}`];

        let volume: number;

        if (month === undefined || month.qty === null) {
          lines.push('Заказов не было');
          volume = 0;
        } else {
          lines.push(`Заказов: ${month.count}`);
          lines.push(`Заказано: ${month.qty.formatSmart(unit)}`);

          if (month.minPrice !== null && month.maxPrice !== null) {
            if (month.minPrice.equals(month.maxPrice) === true) {
              lines.push(`Цена: ${month.minPrice.format(displayCurrency, unit)}`);
            } else {
              lines.push(`Цена: ${month.minPrice.format(displayCurrency)} — ${month.maxPrice.format(displayCurrency, unit)}`);
              lines.push(`Средневзвешенная: ${Price.fromOperation(month.pricedAmount.div(month.pricedQty)).format(displayCurrency, unit)}`);
            }
          }

          volume = month.qty.toNumber();
        }

        const tendersCount = monthlyTendersCount.get(start.getTime());

        if (tendersCount !== undefined) {
          lines.push(`Тендеров: ${tendersCount}`);
        }

        // Клик по месяцу раскрывает таблицу данных, отфильтрованную по нему
        const monthStart = start;

        result.orderVolumes.push(new ChartBar(start, end, volume, lines.join('\n'), new ChartAction('', () => this.selectMonth(monthStart))));
      }

      // Тендер: стартовая цена → лучшая ставка на дату закрытия, обе по курсу на начало тендера.
      // Снизили — яркий отрезок; не снизили или ставок не было — блеклый
      for (const tender of this.tenders) {
        const startAt = new DateSimple(tender.startAt);
        const endAt = new DateSimple(tender.endAt);
        const startPrice = convert(tender.priceStartFull, tender.priceStartCurrencyCode, startAt);

        let bestPrice: Price | null = null;

        if (tender.bestBidPrice !== null && tender.bestBidCurrencyCode !== null) {
          bestPrice = convert(tender.bestBidPrice, tender.bestBidCurrencyCode, startAt);
        }

        const hasBids = tender.bestBidPrice !== null;
        const periodText = `${startAt.toString()} — ${endAt.toString()}`;

        let bestPriceText: string;
        let diffText = '';

        if (hasBids === false) {
          bestPriceText = 'ставок не было';
        } else if (bestPrice === null) {
          bestPriceText = '✕';
        } else {
          bestPriceText = bestPrice.format(displayCurrency, unit);
        }

        // Ставка есть, но её курс ещё едет или его нет — тендер целиком не рисуем, чтобы не выдать его за несостоявшийся
        if (startPrice !== null && (hasBids === false || bestPrice !== null)) {
          const lines = [
            `Тендер № ${tender.id}`,
            `Период: ${periodText}`,
            `Стартовая цена: ${startPrice.format(displayCurrency, unit)}`,
          ];

          let isMuted: boolean;

          if (bestPrice === null) {
            lines.push('Ставок не было');
            isMuted = true;
          } else {
            const diffPercent = (bestPrice.toNumber() - startPrice.toNumber()) / startPrice.toNumber() * 100;

            let sign: string;

            if (diffPercent < 0) {
              sign = '−';
            } else {
              sign = '+';
            }

            diffText = `${sign}${Math.abs(diffPercent).toFixed(1).replace('.', ',')}%`;

            lines.push(`Лучшая ставка: ${bestPrice.format(displayCurrency, unit)} (${diffText})`);
            isMuted = bestPrice.greaterThanOrEqualTo(startPrice);
          }

          lines.push(`Участников: ${tender.sellersCount}`);

          if (tender.priceStartCurrencyCode !== displayCurrency.code || (tender.bestBidCurrencyCode !== null && tender.bestBidCurrencyCode !== displayCurrency.code)) {
            lines.push(`Курс на: ${startAt.toString()}`);
          }

          result.tenders.push(new ChartRange(
            endAt,
            startPrice.toNumber(),
            bestPrice?.toNumber() ?? null,
            isMuted,
            lines.join('\n'),
            new ChartAction(String(tender.id), () => TendersService.showTenderCardById(tender.id)),
          ));
        }

        result.tenderRows.push({ tender, period: periodText, startPrice, bestPriceText, diffText });
      }

      // Бюджетная цена — по курсу на сегодня
      const today = DateSimple.today();

      for (const budgetPrice of this.budgetPrices) {
        const currencyCode = budgetPrice.currency?.code;
        const start = budgetPrice.validityPeriod.start;

        if (currencyCode !== undefined && start !== null) {
          const price = convert(budgetPrice.price, currencyCode, today);

          if (price !== null) {
            const end = budgetPrice.validityPeriod.end;

            // Конец периода приходит включительным, ступени нужен исключающий — иначе соседние ступени не стыкуются
            let stepEnd: DateSimple | null;
            let periodText: string;

            if (end === null) {
              stepEnd = null;
              periodText = `с ${start.toString()}`;
            } else {
              stepEnd = end.addDays(1);
              periodText = `${start.toString()} — ${end.toString()}`;
            }

            result.budgetSteps.push(new ChartStep(start, stepEnd, price.toNumber(), `Период: ${periodText}\nЦена: ${price.format(displayCurrency, unit)}`));
          }
        }
      }

      return result;
    },

    /** Период таблицы данных: выбранный месяц или весь период графика; правая граница не входит */
    tableRange(): { from: DateSimple; to: DateSimple } {
      if (this.selectedMonth === null) {
        return { from: this.period.from, to: this.period.to.addDays(1) };
      } else {
        return { from: this.selectedMonth, to: this.selectedMonth.addMonths(1) };
      }
    },

    tableOrders(): { order: OrderPricePoint; price: Price | null }[] {
      return this.chart.orderRows.filter(row =>
        row.order.createdAt.getTime() >= this.tableRange.from.getTime() && row.order.createdAt.getTime() < this.tableRange.to.getTime()
      );
    },

    tableTenders(): { tender: TenderPricePoint; period: string; startPrice: Price | null; bestPriceText: string; diffText: string }[] {
      return this.chart.tenderRows.filter(row =>
        row.tender.endAt.getTime() >= this.tableRange.from.getTime() && row.tender.endAt.getTime() < this.tableRange.to.getTime()
      );
    },
  },

  methods: {
    /** Клик по месяцу на графике: таблица данных раскрывается и фильтруется по нему */
    selectMonth(month: DateSimple): void {
      this.selectedMonth = month;
      this.isDataExpanded = true;
    },

    toggleData(): void {
      this.isDataExpanded = this.isDataExpanded === false;
    },
  },
})
</script>
